import React, { useState } from 'react';
import Loading from '@99designs/apps/spa/common/Loading';
import { Source } from '@99designs/feature-auth';
import getCsrfToken from '@99designs/frontend-components/utils/app/getCsrfToken';

import Login, { LoginProps } from './Login';
import Masquerading from './Masquerading';
import SignUp, { SignUpProps } from './SignUp';
import VerifyLogin from './VerifyTwoFactor/VerifyLogin';
import { PageType } from './types';
import TermsUpdate from './TermsUpdate';

export interface ContainerProps {
    clientError?: string;
    email?: string;
    loading?: boolean;
    onSetPage: (
        e: React.MouseEvent<HTMLAnchorElement> | null,
        page: PageType,
        link: string | null
    ) => void;
    page: PageType;
    recaptchaSkipToken?: string;
    resetSuccessReason?: string;
    showRecaptcha?: boolean;
    source: Source;
    target?: string;
    variant?: 'designer' | 'invite';
}

function Container({
    clientError,
    email,
    loading,
    onSetPage,
    page,
    recaptchaSkipToken,
    resetSuccessReason,
    showRecaptcha = true,
    source,
    target,
    variant,
}: ContainerProps) {
    const [postLoginUrl, setPostLoginUrl] = useState('');

    if (loading) {
        return <Loading />;
    }

    function handleSubmit(endpoint: 'login' | 'signup', data: Record<string, unknown>) {
        const query = new URLSearchParams({
            ...(target && { target }),
            ...(recaptchaSkipToken && { 'recaptcha-skip-token': recaptchaSkipToken }),
        });

        const url = new URL(`/accounts/xhr/${endpoint}`, window.location.origin);
        url.search = query.toString();

        return fetch(url.toString(), {
            method: 'POST',
            headers: { Accept: 'application/json' },
            credentials: 'same-origin',
            body: JSON.stringify({
                source,
                csrf_token: getCsrfToken(),
                ...data,
            }),
        })
            .then((response) => response.json().then((d) => ({ status: response.status, body: d })))
            .then((d) => {
                if (d.status === 200) {
                    if (d.body.must_verify_2fa) {
                        onSetPage(null, 'verifyLogin', d.body.target);
                        return d;
                    }

                    if (d.body.must_view_terms) {
                        setPostLoginUrl(d.body.target);
                        onSetPage(null, 'termsUpdate', null);
                        return d;
                    }

                    if (d.body.target) {
                        window.location.assign(d.body.target);
                    }

                    return d;
                }

                return d;
            });
    }

    const props = {
        onSetPage,
        onSubmit: handleSubmit,
        showRecaptcha,
        source,
        target,
    };

    if (page === 'login') {
        const loginProps: LoginProps = {
            ...props,
            clientError,
            resetSuccessReason,
        };

        return <Login {...loginProps} />;
    } else if (page === 'signup') {
        const signUpProps: SignUpProps = {
            ...props,
            email,
        };

        if (variant === 'invite') {
            return <SignUp hideRoleSelector userRole="designer" {...signUpProps} />;
        } else if (variant === 'designer') {
            return <SignUp userRole="designer" {...signUpProps} />;
        }

        return <SignUp {...signUpProps} />;
    } else if (page === 'verifyLogin') {
        return <VerifyLogin />;
    } else if (page === 'masquerading') {
        return <Masquerading />;
    } else if (page === 'termsUpdate') {
        return <TermsUpdate redirectUrl={postLoginUrl} />;
    }

    return null;
}

export default Container;
